import { type CountryCodeAlpha3, CurrencyCode } from 'common/api/api-types';
import { COUNTRY_CODE } from 'common/constants';
import { useCurrentCompany } from 'features/sidebar';

const locale: Record<CountryCodeAlpha3, string> = {
  [COUNTRY_CODE.GERMANY]: 'de-DE',
  [COUNTRY_CODE.JAPAN]: 'ja-JP',
};

const localeToCurrencyCode: Record<string, CurrencyCode> = {
  'de-DE': CurrencyCode.parse('EUR'),
  'ja-JP': CurrencyCode.parse('JPY'),
  'en-US': CurrencyCode.parse('USD'),
  'en-GB': CurrencyCode.parse('GBP'),
  'is-IS': CurrencyCode.parse('GBP'),
};
const decimalFormat = (region?: string) => Intl.NumberFormat(region ?? 'de-DE');

const decimalFormatWith2DecimalPlaces = (region?: string) =>
  Intl.NumberFormat(region ?? 'de-DE', {
    maximumFractionDigits: 2,
  });

const percentFormat = (region?: string) => {
  const selectedRegion = region ?? 'de-DE';

  return Intl.NumberFormat(selectedRegion, {
    style: 'percent',
    currency: localeToCurrencyCode[selectedRegion] || CurrencyCode.parse('EUR'),
    maximumFractionDigits: 2,
  });
};
const percentWithNoFractionFormat = (region?: string) =>
  Intl.NumberFormat(region ?? 'de-DE', {
    style: 'percent',
    maximumFractionDigits: 0,
  });

const integerFormat = (region?: string) =>
  Intl.NumberFormat(region || 'de-DE', {
    maximumFractionDigits: 0,
  });

export const useNumberFormatter = () => {
  const { currentCompany } = useCurrentCompany();

  const countryCode = currentCompany.data?.countryCode;

  if (!countryCode) {
    return;
  }

  const currentLocale = locale[countryCode];
  return {
    decimalFormat: decimalFormat(currentLocale),
    decimalFormatWith2DecimalPlaces: decimalFormatWith2DecimalPlaces(currentLocale),
    percentFormat: percentFormat(currentLocale),
    percentWithNoFractionFormat: percentWithNoFractionFormat(currentLocale),
    integerFormat: integerFormat(currentLocale),
    currentLocale: currentLocale ?? locale[COUNTRY_CODE.GERMANY],
  };
};

const enUsNumberFormat = Intl.NumberFormat('en-US', {
  maximumFractionDigits: 2,
});

export const lcspPercentFormat = (value?: number) =>
  value !== undefined ? `${enUsNumberFormat.format(value)}%` : '-';
